import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, ChangeDetectorRef, effect } from '@angular/core';
import { ChatService, ChatMessage, Contact } from './chat.service';
import { SettingsService } from '../settings/settings.service';
import { CalendarService, EVENT_CATEGORIES } from '../calendar/calendar.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { AuthService } from '../services/auth.services';

@Component({
  selector: 'app-chat',
  standalone: false,
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
})
export class ChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesContainer', { static: false }) messagesContainer!: ElementRef;
  @ViewChild('messageInput', { static: false }) messageInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef<HTMLInputElement>;

  messages: ChatMessage[] = [];
  messageText: string = '';
  isLoading: boolean = false;
  currentUserName: string = 'User';
  currentUserAvatar: string = '';
  currentUserId: string = '';

  // Contacts and conversations
  contacts: Contact[] = [];
  selectedContact: Contact | null = null;
  currentConversationMessages: ChatMessage[] = [];

  // User search
  showUserSearch: boolean = false;
  showSearchPanelHeader: boolean = true;
  searchQuery: string = '';
  searchResults: any[] = [];
  isSearching: boolean = false;
  connectionRequests: any[] = [];

  // View state
  showContacts: boolean = true;
  showRequests: boolean = false;

  // Group chat creation
  showCreateGroupModal: boolean = false;
  newGroupName: string = '';
  selectedParticipantsForGroup: string[] = [];

  // Group chat editing
  showEditGroupModal: boolean = false;
  editingGroup: Contact | null = null;
  editGroupName: string = '';
  selectedParticipantsForEdit: string[] = [];

  // Poll creation
  showCreatePollModal: boolean = false;
  newPollQuestion: string = '';
  newPollOptions: string[] = ['', ''];
  newPollAllowMultiple: boolean = false;

  // Contact sharing
  showShareContactModal: boolean = false;
  selectedContactToShare: Contact | null = null;

  // Event creation
  showCreateEventModal: boolean = false;
  eventForm = {
    title: '',
    startDate: '',
    startTime: '09:00',
    endDate: '',
    endTime: '10:00',
    allDay: false,
    category: 'meeting',
    description: '',
    location: '',
    reminder: 15
  };
  eventCategories = EVENT_CATEGORIES;

  // Typing indicator
  isOtherUserTyping: boolean = false;
  otherUserTypingName: string = '';

  // File upload
  selectedFile: File | null = null;
  isUploadingFile: boolean = false;
  showAttachMenu: boolean = false;
  showEmojiPicker: boolean = false;

  // Message editing
  editingMessageId: string | null = null;
  editingMessageText: string = '';

  // Auto-complete
  showAutocomplete: boolean = false;
  autocompleteSuggestions: any[] = [];
  autocompleteType: 'mention' | 'emoji' | 'phrase' | null = null;
  selectedSuggestionIndex: number = 0;
  autocompleteQuery: string = '';
  autocompletePosition: { top: number; left: number } = { top: 0, left: 0 };

  // Common phrases for suggestions (loaded from API)
  commonPhrases: string[] = [];
  isLoadingPhrases: boolean = false;

  // Mobile detection
  isMobile: boolean = false;

  // Contact menu state
  openContactMenu: string | null = null;

  // Background context menu state
  showBackgroundMenu: boolean = false;
  backgroundMenuPosition: { top: number; left: number } = { top: 0, left: 0 };
  selectedBackground: string = 'default';

  // Self-destruct message feature
  enableSelfDestruct: boolean = false;
  selfDestructTimers: Map<string, any> = new Map();
  selfDestructMessages: Set<string> = new Set();
  countdownUpdateInterval: any = null;

  // Background options
  backgroundOptions: Array<{ id: string; name: string; value: string; preview: string }> = [
    { id: 'default', name: 'Default', value: '#f0f2f5', preview: '#f0f2f5' },
    { id: 'white', name: 'White', value: '#ffffff', preview: '#ffffff' },
    { id: 'light-blue', name: 'Light Blue', value: '#e3f2fd', preview: '#e3f2fd' },
    { id: 'light-green', name: 'Light Green', value: '#e8f5e9', preview: '#e8f5e9' },
    { id: 'light-purple', name: 'Light Purple', value: '#f3e5f5', preview: '#f3e5f5' },
    { id: 'light-pink', name: 'Light Pink', value: '#fce4ec', preview: '#fce4ec' },
    { id: 'light-yellow', name: 'Light Yellow', value: '#fff9c4', preview: '#fff9c4' },
    { id: 'light-gray', name: 'Light Gray', value: '#f5f5f5', preview: '#f5f5f5' },
    { id: 'warm-white', name: 'Warm White', value: '#fffef7', preview: '#fffef7' },
    { id: 'cool-white', name: 'Cool White', value: '#f8f9fa', preview: '#f8f9fa' }
  ];

  // Common emojis for quick picker
  quickEmojis: { emoji: string; name: string }[] = [
    { emoji: '😀', name: 'grinning' },
    { emoji: '😊', name: 'smile' },
    { emoji: '😂', name: 'laughing' },
    { emoji: '😍', name: 'heart eyes' },
    { emoji: '🥰', name: 'smiling with hearts' },
    { emoji: '😘', name: 'kiss' },
    { emoji: '😉', name: 'wink' },
    { emoji: '😎', name: 'cool' },
    { emoji: '🤗', name: 'hugging' },
    { emoji: '👍', name: 'thumbs up' },
    { emoji: '👎', name: 'thumbs down' },
    { emoji: '❤️', name: 'heart' },
    { emoji: '💕', name: 'two hearts' },
    { emoji: '💖', name: 'sparkling heart' },
    { emoji: '💯', name: 'hundred' },
    { emoji: '🔥', name: 'fire' },
    { emoji: '🎉', name: 'party' },
    { emoji: '✅', name: 'check' },
    { emoji: '❌', name: 'cross' },
    { emoji: '😢', name: 'sad' },
    { emoji: '😮', name: 'surprised' },
    { emoji: '😴', name: 'sleepy' },
    { emoji: '🙏', name: 'pray' },
    { emoji: '👏', name: 'clap' },
    { emoji: '🎊', name: 'confetti' },
    { emoji: '🎈', name: 'balloon' },
    { emoji: '⭐', name: 'star' },
    { emoji: '🌟', name: 'glowing star' },
    { emoji: '💪', name: 'flexed biceps' },
    { emoji: '👀', name: 'eyes' }
  ];

  // Common emojis (kept for autocomplete)
  commonEmojis: { emoji: string; name: string }[] = [
    { emoji: '😀', name: 'grinning' },
    { emoji: '😊', name: 'smile' },
    { emoji: '👍', name: 'thumbs up' },
    { emoji: '❤️', name: 'heart' },
    { emoji: '😂', name: 'laughing' },
    { emoji: '😍', name: 'heart eyes' },
    { emoji: '🙏', name: 'pray' },
    { emoji: '👏', name: 'clap' },
    { emoji: '🎉', name: 'party' },
    { emoji: '🔥', name: 'fire' },
    { emoji: '✅', name: 'check' },
    { emoji: '❌', name: 'cross' },
    { emoji: '😢', name: 'sad' },
    { emoji: '😮', name: 'surprised' },
    { emoji: '😴', name: 'sleepy' }
  ];

  private destroy$ = new Subject<void>();
  private shouldScroll = false;
  private typingDebounceTimer: any = null;
  private clickOutsideHandler?: (event: MouseEvent) => void;
  private searchDebounceTimer: any = null;
  private searchRequestId: number = 0;
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('photoVideoInput', { static: false }) photoVideoInput!: ElementRef<HTMLInputElement>;
  @ViewChild('editInput', { static: false }) editInput!: ElementRef<HTMLTextAreaElement>;

  constructor(
    public chatService: ChatService,
    public auth: AuthService,
    private cdr: ChangeDetectorRef,
    private settingsService: SettingsService,
    private calendarService: CalendarService
  ) {
    // Watch for chat settings changes using effect (must be in constructor)
    effect(() => {
      const chatSettings = this.settingsService.chatSettings();
      if (chatSettings?.background && chatSettings.background !== this.selectedBackground) {
        this.selectedBackground = chatSettings.background;
        // Apply background after a short delay to ensure DOM is ready
        setTimeout(() => {
          this.applyBackground();
        }, 100);
      }
    });
  }

  private resizeHandler = () => this.checkMobile();

  ngOnInit(): void {
    // Detect mobile viewport
    this.checkMobile();
    window.addEventListener('resize', this.resizeHandler);

    // Load saved background preference from settings
    this.loadBackgroundPreference();

    // Close attach menu and emoji picker when clicking outside
    this.clickOutsideHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.attach-button-wrapper')) {
        this.showAttachMenu = false;
      }
      if (!target.closest('.emoji-picker-wrapper')) {
        this.showEmojiPicker = false;
      }
      if (!target.closest('.contact-menu-wrapper')) {
        this.openContactMenu = null;
      }
      if (!target.closest('.background-context-menu')) {
        this.showBackgroundMenu = false;
      }
    };
    document.addEventListener('click', this.clickOutsideHandler);
    document.addEventListener('contextmenu', (e) => {
      // Close background menu if right-clicking elsewhere
      if (!(e.target as HTMLElement).closest('.messages-container') &&
          !(e.target as HTMLElement).closest('.background-context-menu')) {
        this.showBackgroundMenu = false;
      }
    });

    // Get current user from Auth0
    this.auth.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe((profileUser: any) => {
        const user = profileUser?.profile ?? profileUser;
        if (user) {
          const userId = (user._id ?? user.email ?? user.sub ?? '');
          const userName = user.name || 'User';
          const userAvatar = user.picture || '';

          this.currentUserName = userName;
          this.currentUserAvatar = userAvatar;
          this.currentUserId = userId;

          // Initialize chat service with user
          if (userId) {
            this.chatService.setUser(userId, userName, userAvatar);
            this.calendarService.setUserId(userId);
            this.loadConnectionRequests();
            this.loadContacts();
          }
        }
      });

    // Subscribe to messages from service (reactive updates for current conversation)
    this.chatService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(messages => {
        if (!this.selectedContact || !messages.length) return;
        const filtered = this.selectedContact.isGroup && this.selectedContact.groupId
          ? messages.filter((m: ChatMessage) => m.groupId === this.selectedContact!.groupId)
          : messages.filter((m: ChatMessage) => m.conversationId === [this.currentUserId, this.selectedContact!.userId].sort().join('_'));
        if (filtered.length > 0 || !this.selectedContact.isGroup) {
          this.currentConversationMessages = [...filtered];
          this.shouldScroll = true;
          this.cdr.detectChanges();
        }
      });

    // Subscribe to notifications
    this.chatService.notification$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notification => {
        if (notification) {
          this.handleNotification(notification);
        }
      });

    // Subscribe to typing status
    this.chatService.typingStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((typingStatus) => {
        if (typingStatus && this.selectedContact && typingStatus.userId === this.selectedContact.userId) {
          this.isOtherUserTyping = true;
          this.otherUserTypingName = typingStatus.userName || this.selectedContact.name;
        } else {
          this.isOtherUserTyping = false;
          this.otherUserTypingName = '';
        }
      });

    // Start countdown update interval for self-destruct messages
    this.startCountdownUpdate();
  }

  private handleNotification(notification: any): void {
    // Mark as read
    this.chatService.markNotificationAsRead(notification._id);

    // Show toast notification
    if (notification.type === 'connection_request') {
      this.showNotificationToast(
        notification.title,
        notification.message,
        'info'
      );
      // Reload connection requests
      this.loadConnectionRequests();
    } else if (notification.type === 'connection_accepted') {
      this.showNotificationToast(
        notification.title,
        notification.message,
        'success'
      );
      // Reload connection requests and contacts
      this.loadConnectionRequests();
      this.loadContacts();
    } else if (notification.type === 'connection_rejected') {
      this.showNotificationToast(
        notification.title,
        notification.message,
        'info'
      );
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
    // Ensure background persists - apply it if container exists and background isn't set
    const container = this.messagesContainer?.nativeElement;
    if (container) {
      const currentBg = container.style.background || getComputedStyle(container).background;
      const expectedBg = this.getSelectedBackgroundValue();
      // Only apply if background doesn't match (to avoid unnecessary updates)
      if (!currentBg || !currentBg.includes(expectedBg.replace('#', ''))) {
        this.applyBackground();
      }
    }
  }

  ngOnDestroy(): void {
    // Clear typing status if user is typing
    if (this.selectedContact) {
      this.chatService.setTypingStatus(this.selectedContact.userId, false);
    }

    // Clear timers
    if (this.typingDebounceTimer) {
      clearTimeout(this.typingDebounceTimer);
    }

    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    // Clear all self-destruct timers
    this.selfDestructTimers.forEach((timer) => {
      clearTimeout(timer);
    });
    this.selfDestructTimers.clear();
    this.selfDestructMessages.clear();

    // Clear countdown update interval
    if (this.countdownUpdateInterval) {
      clearInterval(this.countdownUpdateInterval);
      this.countdownUpdateInterval = null;
    }

    // Invalidate any pending search requests
    this.searchRequestId++;

    // Remove resize listener
    window.removeEventListener('resize', this.resizeHandler);

    // Remove click outside handler
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Check if device is mobile
   */
  checkMobile(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  /**
   * Go back to contacts list (mobile only)
   */
  goBackToContacts(): void {
    this.clearChat();
  }

  sendMessage(): void {
    const text = this.messageText.trim();
    if ((!text && !this.selectedFile) || this.isLoading || !this.selectedContact) return;

    // Clear typing status
    this.chatService.setTypingStatus(this.selectedContact.userId, false);

    // Clear debounce timer
    if (this.typingDebounceTimer) {
      clearTimeout(this.typingDebounceTimer);
      this.typingDebounceTimer = null;
    }

    // Show loading indicator
    this.isLoading = true;

    const isGroupChat = this.selectedContact?.isGroup && this.selectedContact?.groupId;
    const shouldSelfDestruct = this.enableSelfDestruct;

    // Handle file upload if file is selected
    if (this.selectedFile) {
      this.isUploadingFile = true;
      this.chatService.uploadChatFile(this.selectedFile)
        .then((attachment: any) => {
          // Send message with attachment
          if (isGroupChat) {
            return this.chatService.sendMessageToGroup(this.selectedContact!.groupId!, text, attachment);
          } else {
            return this.chatService.sendMessageToUser(this.selectedContact!.userId, text, attachment);
          }
        })
        .then(async (result: any) => {
          // Clear file and input
          this.selectedFile = null;
          this.messageText = '';
          if (this.fileInput) {
            this.fileInput.nativeElement.value = '';
          }

          // Reload conversation messages
          await this.loadConversation(this.selectedContact!);
          this.isLoading = false;
          this.isUploadingFile = false;

          // Set up self-destruct timer if enabled
          // Find the most recent message sent by current user to set up timer
          if (shouldSelfDestruct) {
            const latestUserMessage = this.currentConversationMessages
              .filter(msg => msg.sender === 'user' && msg.userId === this.currentUserId)
              .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];

            if (latestUserMessage) {
              const messageId = latestUserMessage._id || latestUserMessage.id;
              if (messageId) {
                this.setupSelfDestructTimer(messageId);
              }
            }
          }

          // Focus back on input
          setTimeout(() => {
            this.messageInput?.nativeElement?.focus();
          }, 0);
        })
        .catch((error: any) => {
          console.error('Error sending message with file:', error);
          this.showToast(error.message || 'Failed to send file', 'error');
          this.isLoading = false;
          this.isUploadingFile = false;
        });
    } else {
      // Send text message only
      // Clear input immediately for better UX
      this.messageText = '';

      // Focus back on input
      setTimeout(() => {
        this.messageInput?.nativeElement?.focus();
      }, 0);

      // Send message to selected contact or group
      const sendPromise = isGroupChat
        ? this.chatService.sendMessageToGroup(this.selectedContact.groupId!, text)
        : this.chatService.sendMessageToUser(this.selectedContact.userId, text);

      sendPromise
        .then(async (result: any) => {
          // Reload conversation messages
          await this.loadConversation(this.selectedContact!);
          this.isLoading = false;

          // Set up self-destruct timer if enabled
          // Find the most recent message sent by current user to set up timer
          if (shouldSelfDestruct) {
            const latestUserMessage = this.currentConversationMessages
              .filter(msg => msg.sender === 'user' && msg.userId === this.currentUserId)
              .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];

            if (latestUserMessage) {
              const messageId = latestUserMessage._id || latestUserMessage.id;
              if (messageId) {
                this.setupSelfDestructTimer(messageId);
              }
            }
          }
        })
        .catch((error) => {
          console.error('Error sending message:', error);
          this.showToast('Failed to send message', 'error');
          this.isLoading = false;
        });
    }
  }

  /**
   * Set up self-destruct timer for a message (5 minutes)
   */
  setupSelfDestructTimer(messageId: string): void {
    // Mark message as self-destructing
    this.selfDestructMessages.add(messageId);

    // Clear any existing timer for this message
    if (this.selfDestructTimers.has(messageId)) {
      clearTimeout(this.selfDestructTimers.get(messageId));
    }

    // Set timer for 5 minutes (300000 ms)
    const timer = setTimeout(() => {
      this.fadeAndDeleteMessage(messageId);
    }, 5 * 60 * 1000);

    this.selfDestructTimers.set(messageId, timer);
  }

  /**
   * Fade and delete a self-destructing message
   */
  async fadeAndDeleteMessage(messageId: string): Promise<void> {
    // Find the message in the current conversation
    const message = this.currentConversationMessages.find(
      msg => (msg._id || msg.id) === messageId
    );

    if (!message) {
      // Message not found, clean up
      this.selfDestructTimers.delete(messageId);
      this.selfDestructMessages.delete(messageId);
      return;
    }

    // Add fading class to message (will be handled by CSS)
    // We'll use a data attribute to mark it as fading
    const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
    if (messageElement) {
      messageElement.classList.add('fading-out');
    }

    // Wait for fade animation (500ms), then delete
    setTimeout(async () => {
      try {
        // Delete the message from the server
        const result = await this.chatService.deleteChatMessage(messageId);
        if (result?.code === 200) {
          // Reload conversation to reflect deletion
          if (this.selectedContact) {
            await this.loadConversation(this.selectedContact);
          }
        }
      } catch (error) {
        console.error('Error deleting self-destruct message:', error);
        // Even if deletion fails, remove from UI
        if (this.selectedContact) {
          await this.loadConversation(this.selectedContact);
        }
      } finally {
        // Clean up
        this.selfDestructTimers.delete(messageId);
        this.selfDestructMessages.delete(messageId);
      }
    }, 500);
  }

  /**
   * Check if a message is set to self-destruct
   */
  isSelfDestructingMessage(messageId: string | undefined): boolean {
    if (!messageId) return false;
    return this.selfDestructMessages.has(messageId);
  }

  /**
   * Get remaining time for a self-destructing message (in seconds)
   */
  getSelfDestructRemainingTime(message: ChatMessage): number | null {
    const messageId = message._id || message.id;
    if (!messageId || !this.selfDestructMessages.has(messageId)) {
      return null;
    }

    // Calculate time since message was created
    const messageTime = new Date(message.timestamp).getTime();
    const now = Date.now();
    const elapsed = now - messageTime;
    const totalTime = 5 * 60 * 1000; // 5 minutes in ms
    const remaining = Math.max(0, totalTime - elapsed);

    return Math.floor(remaining / 1000); // Return in seconds
  }

  /**
   * Start periodic countdown update for self-destruct messages
   */
  startCountdownUpdate(): void {
    // Update countdown every second
    this.countdownUpdateInterval = setInterval(() => {
      if (this.selfDestructMessages.size > 0) {
        // Trigger change detection to update countdown display
        this.cdr.detectChanges();
      }
    }, 1000);
  }

  /**
   * Select a contact and load conversation
   */
  async selectContact(contact: Contact): Promise<void> {
    this.selectedContact = contact;
    await this.loadConversation(contact);

    // Re-apply background when contact is selected
    setTimeout(() => {
      this.applyBackground();
    }, 100);

    // On mobile, the view will automatically switch due to CSS classes
  }

  /**
   * Load conversation messages with a contact or group chat
   */
  async loadConversation(contact: Contact): Promise<void> {
    try {
      if (contact?.isGroup && contact?.groupId) {
        // Load group chat messages
        this.currentConversationMessages = await this.chatService.getGroupChatMessages(contact.groupId);
      } else {
        // Load one-on-one conversation messages
        this.currentConversationMessages = await this.chatService.getConversationMessages(contact.userId);
      }
      this.shouldScroll = true;
      this.cdr.detectChanges();

      // Check for any self-destructing messages that need timers set up
      this.currentConversationMessages.forEach((message) => {
        const messageId = message._id || message.id;
        if (messageId && this.selfDestructMessages.has(messageId)) {
          // Check if message is still within 5 minutes
          const messageTime = new Date(message.timestamp).getTime();
          const now = Date.now();
          const elapsed = now - messageTime;
          const totalTime = 5 * 60 * 1000; // 5 minutes

          if (elapsed < totalTime) {
            // Message still has time, set up timer for remaining time
            const remaining = totalTime - elapsed;
            if (!this.selfDestructTimers.has(messageId)) {
              const timer = setTimeout(() => {
                this.fadeAndDeleteMessage(messageId);
              }, remaining);
              this.selfDestructTimers.set(messageId, timer);
            }
          } else {
            // Message has expired, delete it immediately
            this.fadeAndDeleteMessage(messageId);
          }
        }
      });

      // Mark messages as read when viewing conversation (only for one-on-one chats)
      if (!contact?.isGroup) {
        try {
          await this.chatService.markMessagesAsRead(contact.userId);
          // Reload conversation to get updated read status
          this.currentConversationMessages = await this.chatService.getConversationMessages(contact.userId);
          this.cdr.detectChanges();
        } catch (error) {
          console.error('Error marking messages as read:', error);
        }
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      this.currentConversationMessages = [];
      this.cdr.detectChanges();
    }
  }

  /**
   * Load contacts (accepted connections) and group chats
   */
  async loadContacts(): Promise<void> {
    try {
      const loadedContacts = await this.chatService.getAcceptedConnections();

      // Load group chats
      const groupChats = await this.chatService.getGroupChats();

      // Convert group chats to Contact format
      const groupContacts: Contact[] = groupChats
        .filter(group => group._id) // Only include groups with valid IDs
        .map(group => ({
          userId: group._id || '',
          name: group.name,
          avatar: group.avatar || '',
          email: group._id || '',
          isGroup: true,
          groupId: group._id || '', // Ensure groupId is always a string
          participants: group.participants.map(p => ({
            userId: p.userId,
            userName: p.userName,
            userAvatar: p.userAvatar
          }))
        }));

      // Merge contacts and group chats, sort by last message time
      const allContacts = [...loadedContacts, ...groupContacts];
      // Force change detection by creating a new array
      this.contacts = allContacts;
    } catch (error) {
      console.error('Error loading contacts:', error);
      this.contacts = [];
    }
  }

  onKeyPress(event: KeyboardEvent): void {
    // Handle autocomplete navigation
    if (this.showAutocomplete) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.selectedSuggestionIndex = Math.min(
          this.selectedSuggestionIndex + 1,
          this.autocompleteSuggestions.length - 1
        );
        return;
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.selectedSuggestionIndex = Math.max(this.selectedSuggestionIndex - 1, 0);
        return;
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        if (this.autocompleteSuggestions.length > 0) {
          event.preventDefault();
          this.selectSuggestion(this.autocompleteSuggestions[this.selectedSuggestionIndex]);
          return;
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.hideAutocomplete();
        return;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey && !this.showAutocomplete) {
      event.preventDefault();
      this.sendMessage();
    } else if (this.selectedContact) {
      // User is typing - send typing status
      this.handleTyping();
    }
  }

  /**
   * Handle typing input - debounced to avoid too many server calls
   */
  handleTyping(): void {
    if (!this.selectedContact) return;

    // Clear existing timer
    if (this.typingDebounceTimer) {
      clearTimeout(this.typingDebounceTimer);
    }

    // Send typing status
    this.chatService.setTypingStatus(this.selectedContact.userId, true);

    // Set timer to stop typing after 2 seconds of inactivity
    this.typingDebounceTimer = setTimeout(() => {
      this.chatService.setTypingStatus(this.selectedContact!.userId, false);
      this.typingDebounceTimer = null;
    }, 2000);
  }

  /**
   * Handle input changes for typing indicator and autocomplete
   */
  onInputChange(): void {
    // Check for autocomplete triggers
    this.checkAutocomplete();

    if (this.selectedContact && this.messageText.trim()) {
      this.handleTyping();
    } else if (this.selectedContact && !this.messageText.trim()) {
      // Clear typing status if input is empty
      if (this.typingDebounceTimer) {
        clearTimeout(this.typingDebounceTimer);
        this.typingDebounceTimer = null;
      }
      this.chatService.setTypingStatus(this.selectedContact.userId, false);
    }
  }

  /**
   * Check if autocomplete should be shown
   */
  checkAutocomplete(): void {
    const text = this.messageText;
    const cursorPos = this.messageInput?.nativeElement?.selectionStart || text.length;
    const textBeforeCursor = text.substring(0, cursorPos);

    // Check for @ mentions
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      this.autocompleteQuery = mentionMatch[1].toLowerCase();
      this.autocompleteType = 'mention';
      this.loadMentionSuggestions();
      this.updateAutocompletePosition();
      return;
    }

    // Check for : emoji
    const emojiMatch = textBeforeCursor.match(/:(\w*)$/);
    if (emojiMatch) {
      this.autocompleteQuery = emojiMatch[1].toLowerCase();
      this.autocompleteType = 'emoji';
      this.loadEmojiSuggestions();
      this.updateAutocompletePosition();
      return;
    }

    // Check for phrase suggestions (when typing common words)
    if (textBeforeCursor.length > 2 && !textBeforeCursor.includes('@') && !textBeforeCursor.includes(':')) {
      const words = textBeforeCursor.trim().toLowerCase().split(/\s+/);
      const lastWord = words[words.length - 1];

      if (lastWord.length >= 2) {
        this.autocompleteQuery = lastWord;
        this.autocompleteType = 'phrase';
        this.loadPhraseSuggestions();
        if (this.autocompleteSuggestions.length > 0) {
          this.updateAutocompletePosition();
          return;
        }
      }
    }

    // Hide autocomplete if no match
    this.hideAutocomplete();
  }

  /**
   * Load mention suggestions
   */
  loadMentionSuggestions(): void {
    if (!this.selectedContact || !this.contacts) {
      this.hideAutocomplete();
      return;
    }

    const query = this.autocompleteQuery;
    const suggestions = this.contacts
      .filter(contact => {
        const name = (contact.name || contact.email || '').toLowerCase();
        const email = (contact.email || '').toLowerCase();
        return name.includes(query) || email.includes(query);
      })
      .slice(0, 5)
      .map(contact => ({
        type: 'mention',
        value: `@${contact.name || contact.email}`,
        display: contact.name || contact.email,
        email: contact.email,
        avatar: contact.avatar
      }));

    this.autocompleteSuggestions = suggestions;
    this.selectedSuggestionIndex = 0;
    this.showAutocomplete = suggestions.length > 0;
  }

  /**
   * Load emoji suggestions
   */
  loadEmojiSuggestions(): void {
    const query = this.autocompleteQuery;
    const suggestions = this.commonEmojis
      .filter(item => item.name.toLowerCase().includes(query))
      .slice(0, 8)
      .map(item => ({
        type: 'emoji',
        value: item.emoji,
        display: `${item.emoji} :${item.name}:`,
        emoji: item.emoji,
        name: item.name
      }));

    this.autocompleteSuggestions = suggestions;
    this.selectedSuggestionIndex = 0;
    this.showAutocomplete = suggestions.length > 0;
  }

  /**
   * Load phrase suggestions
   */
  loadPhraseSuggestions(): void {
    const query = this.autocompleteQuery;
    const suggestions = this.commonPhrases
      .filter(phrase => phrase.toLowerCase().includes(query))
      .slice(0, 5)
      .map(phrase => ({
        type: 'phrase',
        value: phrase,
        display: phrase
      }));

    this.autocompleteSuggestions = suggestions;
    this.selectedSuggestionIndex = 0;
    this.showAutocomplete = suggestions.length > 0;
  }

  /**
   * Update autocomplete dropdown position
   */
  updateAutocompletePosition(): void {
    if (!this.messageInput?.nativeElement) {
      return;
    }

    const input = this.messageInput.nativeElement;
    const rect = input.getBoundingClientRect();
    const container = input.closest('.input-container');
    const containerRect = container?.getBoundingClientRect();

    if (containerRect) {
      this.autocompletePosition = {
        top: rect.bottom - containerRect.top + 4,
        left: rect.left - containerRect.left
      };
    }
  }

  /**
   * Select a suggestion
   */
  selectSuggestion(suggestion: any): void {
    if (!suggestion || !this.messageInput?.nativeElement) {
      return;
    }

    const input = this.messageInput.nativeElement;
    const text = this.messageText;
    const cursorPos = input.selectionStart || text.length;
    const textBeforeCursor = text.substring(0, cursorPos);

    // Find the trigger pattern
    let match: RegExpMatchArray | null = null;
    let triggerLength = 0;

    if (this.autocompleteType === 'mention') {
      match = textBeforeCursor.match(/@(\w*)$/);
      triggerLength = match ? match[0].length : 0;
    } else if (this.autocompleteType === 'emoji') {
      match = textBeforeCursor.match(/:(\w*)$/);
      triggerLength = match ? match[0].length : 0;
    } else if (this.autocompleteType === 'phrase') {
      const words = textBeforeCursor.trim().split(/\s+/);
      const lastWord = words[words.length - 1] || '';
      triggerLength = lastWord.length;
    }

    if (triggerLength > 0) {
      const startPos = cursorPos - triggerLength;
      const endPos = cursorPos;
      const before = text.substring(0, startPos);
      const after = text.substring(endPos);

      // Insert suggestion
      let insertText = suggestion.value;
      if (this.autocompleteType === 'phrase') {
        insertText = suggestion.value + ' ';
      } else if (this.autocompleteType === 'emoji') {
        insertText = suggestion.emoji + ' ';
      } else if (this.autocompleteType === 'mention') {
        insertText = suggestion.value + ' ';
      }

      this.messageText = before + insertText + after;

      // Set cursor position
      setTimeout(() => {
        const newPos = startPos + insertText.length;
        input.setSelectionRange(newPos, newPos);
        input.focus();
      }, 0);
    }

    this.hideAutocomplete();
  }

  /**
   * Hide autocomplete dropdown
   */
  hideAutocomplete(): void {
    this.showAutocomplete = false;
    this.autocompleteSuggestions = [];
    this.autocompleteType = null;
    this.selectedSuggestionIndex = 0;
    this.autocompleteQuery = '';
  }

  /**
   * Handle input blur
   */
  onInputBlur(): void {
    // Delay hiding to allow clicking on suggestions
    setTimeout(() => {
      if (!document.activeElement?.closest('.autocomplete-dropdown')) {
        this.hideAutocomplete();
      }
    }, 200);
  }

  /**
   * Load common phrases from API
   */
  async loadCommonPhrases(): Promise<void> {
    this.isLoadingPhrases = true;
    try {
      const phrases = await this.chatService.getCommonPhrases();
      this.commonPhrases = phrases;
    } catch (error) {
      console.error('Error loading common phrases:', error);
      // Fallback to empty array, service will handle defaults
      this.commonPhrases = [];
    } finally {
      this.isLoadingPhrases = false;
    }
  }

  /**
   * Toggle attach menu
   */
  toggleAttachMenu(): void {
    this.showAttachMenu = !this.showAttachMenu;
    if (this.showAttachMenu) {
      this.showEmojiPicker = false; // Close emoji picker if open
    }
  }

  /**
   * Toggle emoji picker
   */
  toggleEmojiPicker(): void {
    this.showEmojiPicker = !this.showEmojiPicker;
    if (this.showEmojiPicker) {
      this.showAttachMenu = false; // Close attach menu if open
    }
  }

  /**
   * Insert emoji into message
   */
  insertEmoji(emoji: string): void {
    const input = this.messageInput?.nativeElement;
    if (!input) return;

    const cursorPos = input.selectionStart || this.messageText.length;
    const textBefore = this.messageText.substring(0, cursorPos);
    const textAfter = this.messageText.substring(cursorPos);

    this.messageText = textBefore + emoji + textAfter;

    // Set cursor position after inserted emoji
    setTimeout(() => {
      const newPos = cursorPos + emoji.length;
      input.setSelectionRange(newPos, newPos);
      input.focus();
    }, 0);

    // Close emoji picker after insertion
    this.showEmojiPicker = false;

    // Trigger input change for typing indicator
    this.onInputChange();
  }

  /**
   * Select an attach option
   */
  selectAttachOption(option: string): void {
    this.showAttachMenu = false;

    switch (option) {
      case 'file':
        this.fileInput?.nativeElement?.click();
        break;
      case 'photo-video':
        this.photoVideoInput?.nativeElement?.click();
        break;
      case 'contact':
        this.handleContactOption();
        break;
      case 'poll':
        this.toggleCreatePollModal();
        break;
      case 'event':
        this.handleEventOption();
        break;
    }
  }

  /**
   * Handle contact option
   */
  handleContactOption(): void {
    if (!this.selectedContact) {
      this.showToast('Please select a contact first', 'error');
      return;
    }
    this.showShareContactModal = true;
  }

  /**
   * Toggle share contact modal
   */
  toggleShareContactModal(): void {
    this.showShareContactModal = !this.showShareContactModal;
    if (!this.showShareContactModal) {
      this.selectedContactToShare = null;
    }
  }

  /**
   * Share a contact
   */
  async shareContact(contactToShare: Contact): Promise<void> {
    if (!this.selectedContact) {
      this.showToast('Please select a contact first', 'error');
      return;
    }

    if (!contactToShare) {
      this.showToast('Please select a contact to share', 'error');
      return;
    }

    try {
      const isGroupChat = this.selectedContact?.isGroup && this.selectedContact?.groupId;
      const toUserId = isGroupChat ? null : this.selectedContact.userId;
      const groupId = isGroupChat ? this.selectedContact.groupId : null;

      const result = await this.chatService.shareContact(
        toUserId,
        groupId || null,
        contactToShare
      );

      if (result?.code === 201) {
        this.showToast('Contact shared successfully!', 'success');
        this.toggleShareContactModal();
        // Reload conversation to show the shared contact
        if (this.selectedContact) {
          await this.loadConversation(this.selectedContact);
        }
      } else {
        this.showToast(result?.message || 'Failed to share contact', 'error');
      }
    } catch (error) {
      console.error('Error sharing contact:', error);
      this.showToast('Failed to share contact');
    }
  }

  /**
   * Handle add event option from contact menu
   */
  handleAddEventOption(contact: Contact): void {
    if (!contact) {
      this.showToast('Contact not found', 'error');
      return;
    }

    // Pre-populate event form with contact information
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    this.eventForm = {
      title: `Meeting with ${contact.name}`,
      startDate: this.formatDateForInput(today),
      startTime: '09:00',
      endDate: this.formatDateForInput(tomorrow),
      endTime: '10:00',
      allDay: false,
      category: 'meeting',
      description: `Event with ${contact.name}${contact.email ? ` (${contact.email})` : ''}`,
      location: '',
      reminder: 15
    };

    this.showCreateEventModal = true;
  }

  /**
   * Toggle create event modal
   */
  toggleCreateEventModal(): void {
    this.showCreateEventModal = !this.showCreateEventModal;
    if (!this.showCreateEventModal) {
      this.resetEventForm();
    }
  }

  /**
   * Reset event form
   */
  resetEventForm(): void {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    this.eventForm = {
      title: '',
      startDate: this.formatDateForInput(today),
      startTime: '09:00',
      endDate: this.formatDateForInput(tomorrow),
      endTime: '10:00',
      allDay: false,
      category: 'meeting',
      description: '',
      location: '',
      reminder: 15
    };
  }

  /**
   * Format date for input field (YYYY-MM-DD)
   */
  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Combine date and time strings into a Date object
   */
  combineDateAndTime(dateStr: string, timeStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    const [hours, minutes] = timeStr.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  }

  /**
   * Create event
   */
  async createEvent(): Promise<void> {
    if (!this.eventForm.title.trim()) {
      this.showToast('Please enter an event title', 'error');
      return;
    }

    if (!this.eventForm.startDate || !this.eventForm.endDate) {
      this.showToast('Please select start and end dates', 'error');
      return;
    }

    try {
      const startDateTime = this.combineDateAndTime(
        this.eventForm.startDate,
        this.eventForm.allDay ? '00:00' : this.eventForm.startTime
      );
      const endDateTime = this.combineDateAndTime(
        this.eventForm.endDate,
        this.eventForm.allDay ? '23:59' : this.eventForm.endTime
      );

      if (endDateTime < startDateTime) {
        this.showToast('End date/time must be after start date/time');
        return;
      }

      const categoryColor = this.calendarService.getCategoryColor(this.eventForm.category);

      const eventData = {
        title: this.eventForm.title.trim(),
        startDate: startDateTime,
        endDate: endDateTime,
        allDay: this.eventForm.allDay,
        category: this.eventForm.category,
        color: categoryColor,
        description: this.eventForm.description.trim(),
        location: this.eventForm.location.trim(),
        reminder: this.eventForm.reminder
      };

      this.calendarService.addEvent(eventData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.showToast('Event created successfully!', 'success');
            this.toggleCreateEventModal();
          },
          error: (error) => {
            console.error('Error creating event:', error);
            this.showToast('Failed to create event', 'error');
          }
        });
    } catch (error) {
      console.error('Error creating event:', error);
      this.showToast('Failed to create event', 'error');
    }
  }

  /**
   * Toggle create poll modal
   */
  toggleCreatePollModal(): void {
    this.showCreatePollModal = !this.showCreatePollModal;
    if (!this.showCreatePollModal) {
      // Reset form when closing
      this.newPollQuestion = '';
      this.newPollOptions = ['', ''];
      this.newPollAllowMultiple = false;
    }
  }

  /**
   * Add a new poll option
   */
  addPollOption(): void {
    if (this.newPollOptions.length < 10) {
      this.newPollOptions.push('');
    }
  }

  /**
   * Remove a poll option
   */
  removePollOption(index: number): void {
    if (this.newPollOptions.length > 2) {
      this.newPollOptions.splice(index, 1);
    }
  }

  /**
   * Check if there are enough valid poll options
   */
  hasValidPollOptions(): boolean {
    if (!this.newPollOptions || this.newPollOptions.length === 0) {
      return false;
    }
    const validOptions = this.newPollOptions.filter(opt => opt && opt.trim().length > 0);
    return validOptions.length >= 2;
  }

  /**
   * Create a poll
   */
  async createPoll(): Promise<void> {
    if (!this.newPollQuestion.trim()) {
      this.showToast('Please enter a poll question', 'error');
      return;
    }

    const validOptions = this.newPollOptions.filter(opt => opt.trim().length > 0);
    if (validOptions.length < 2) {
      this.showToast('Please provide at least 2 poll options', 'error');
      return;
    }

    if (!this.selectedContact) {
      this.showToast('Please select a contact first', 'error');
      return;
    }

    try {
      const isGroupChat = this.selectedContact?.isGroup && this.selectedContact?.groupId;
      const toUserId = isGroupChat ? null : this.selectedContact.userId;
      const groupId = isGroupChat ? this.selectedContact.groupId : null;

      const result = await this.chatService.createPollMessage(
        toUserId,
        groupId || null,
        this.newPollQuestion.trim(),
        validOptions,
        this.newPollAllowMultiple
      );

      if (result?.code === 201) {
        this.showToast('Poll created successfully!', 'success');
        this.toggleCreatePollModal();
        // Reload conversation to show the new poll
        if (this.selectedContact) {
          await this.loadConversation(this.selectedContact);
        }
      } else {
        this.showToast(result?.message || 'Failed to create poll', 'error');
      }
    } catch (error) {
      console.error('Error creating poll:', error);
      this.showToast('Failed to create poll');
    }
  }

  /**
   * Vote on a poll option
   */
  async voteOnPoll(message: ChatMessage, optionId: string): Promise<void> {
    if (!message.poll || !message._id) {
      return;
    }

    // Check if poll has expired
    if (message.poll.expiresAt && new Date(message.poll.expiresAt) < new Date()) {
      this.showToast('This poll has expired', 'error');
      return;
    }

    // Check if user has already voted (for single-vote polls)
    const hasVoted = this.hasVotedForOption(message.poll, optionId);
    if (!message.poll.allowMultiple && hasVoted) {
      // For single-vote polls, user can change their vote
      // The backend will handle removing the previous vote
    }

    try {
      const result = await this.chatService.voteOnPoll(message._id, [optionId]);
      if (result?.code === 200) {
        // Reload conversation to show updated poll results
        if (this.selectedContact) {
          await this.loadConversation(this.selectedContact);
        }
      } else {
        this.showToast(result?.message || 'Failed to vote on poll', 'error');
      }
    } catch (error) {
      console.error('Error voting on poll:', error);
      this.showToast('Failed to vote on poll', 'error');
    }
  }

  /**
   * Check if current user has voted for a specific option
   */
  hasVotedForOption(poll: any, optionId: string): boolean {
    if (!poll || !poll.options) {
      return false;
    }
    const option = poll.options.find((opt: any) => opt.id === optionId);
    return option && option.voters && option.voters.includes(this.currentUserId);
  }

  /**
   * Get poll percentage for an option
   */
  getPollPercentage(votes: number, totalVotes: number): number {
    if (totalVotes === 0) {
      return 0;
    }
    return Math.round((votes / totalVotes) * 100);
  }

  /**
   * Check if a poll has expired (same logic for both users so status is consistent)
   */
  isPollExpired(message: ChatMessage): boolean {
    if (!message?.poll?.expiresAt) {
      return false;
    }
    return new Date(message.poll.expiresAt).getTime() < Date.now();
  }

  /**
   * Format poll expiry date
   */
  formatPollExpiry(expiresAt: Date): string {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diff = expiry.getTime() - now.getTime();
    if (diff <= 0) {
      return 'Closed';
    }
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) {
      return `in ${days} ${days === 1 ? 'day' : 'days'}`;
    } else if (hours > 0) {
      return `in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    } else {
      return 'soon';
    }
  }

  /**
   * Handle event option
   */
  handleEventOption(): void {
    if (this.selectedContact) {
      this.handleAddEventOption(this.selectedContact);
    }
  }

  /**
   * Handle file selection
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Check file size (2MB limit)
      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        this.showToast('File size exceeds 2MB limit');
        input.value = '';
        return;
      }

      this.selectedFile = file;
    }
  }

  /**
   * Handle photo/video selection
   */
  onPhotoVideoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Check file size (2MB limit)
      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        this.showToast('File size exceeds 2MB limit');
        input.value = '';
        return;
      }

      this.selectedFile = file;
    }
  }

  /**
   * Remove selected file
   */
  removeSelectedFile(): void {
    this.selectedFile = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  /**
   * Get file size in readable format
   */
  getFileSize(size: number): string {
    if (size < 1024) {
      return size + ' B';
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(1) + ' KB';
    } else {
      return (size / (1024 * 1024)).toFixed(1) + ' MB';
    }
  }

  /**
   * Download file attachment
   */
  downloadFile(attachment: any): void {
    try {
      // Create blob from base64 data
      const byteCharacters = atob(attachment.fileData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: attachment.mimeType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      this.showToast('Failed to download file', 'error');
    }
  }

  /**
   * Get file icon based on mime type
   */
  getFileIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) {
      return 'image';
    } else if (mimeType.startsWith('video/')) {
      return 'video';
    } else if (mimeType.startsWith('audio/')) {
      return 'audio';
    } else if (mimeType.includes('pdf')) {
      return 'pdf';
    } else if (mimeType.includes('word') || mimeType.includes('document')) {
      return 'document';
    } else if (mimeType.includes('sheet') || mimeType.includes('excel')) {
      return 'spreadsheet';
    } else {
      return 'file';
    }
  }

  /**
   * Start editing a message
   */
  startEditingMessage(message: ChatMessage): void {
    if (message.sender !== 'user' || message.userId !== this.currentUserId) {
      return; // Only allow editing own messages
    }

    this.editingMessageId = message._id || message.id || null;
    this.editingMessageText = message.text;

    // Focus on edit input after a short delay
    setTimeout(() => {
      this.editInput?.nativeElement?.focus();
      this.editInput?.nativeElement?.select();
    }, 100);
  }

  /**
   * Cancel editing message
   */
  cancelEditingMessage(): void {
    this.editingMessageId = null;
    this.editingMessageText = '';
  }

  /**
   * Save edited message
   */
  async saveEditedMessage(): Promise<void> {
    if (!this.editingMessageId || !this.editingMessageText.trim()) {
      return;
    }

    try {
      const result = await this.chatService.updateChatMessage(this.editingMessageId, this.editingMessageText.trim());
      if (result?.code === 200) {
        // Reload conversation to show updated message
        if (this.selectedContact) {
          await this.loadConversation(this.selectedContact);
        }
        this.cancelEditingMessage();
        this.showToast('Message updated', 'success');
      } else {
        this.showToast('Failed to update message', 'error');
      }
    } catch (error) {
      console.error('Error updating message:', error);
      this.showToast('Failed to update message', 'error');
    }
  }

  /**
   * Check if message is being edited
   */
  isEditingMessage(messageId: string | undefined): boolean {
    return this.editingMessageId === messageId;
  }

  /**
   * Check if message can be edited (own message, no attachment, within reasonable time)
   */
  canEditMessage(message: ChatMessage): boolean {
    if (message.sender !== 'user' || message.userId !== this.currentUserId) {
      return false;
    }
    if (message.attachment) {
      return false; // Don't allow editing messages with attachments
    }
    // Allow editing messages from the last 24 hours
    const messageAge = Date.now() - new Date(message.timestamp).getTime();
    const maxEditAge = 24 * 60 * 60 * 1000; // 24 hours
    return messageAge < maxEditAge;
  }

  /**
   * Check if message can be deleted (own message, within reasonable time)
   */
  canDeleteMessage(message: ChatMessage): boolean {
    if (message.sender !== 'user' || message.userId !== this.currentUserId) {
      return false;
    }
    // Allow deleting messages from the last 24 hours
    const messageAge = Date.now() - new Date(message.timestamp).getTime();
    const maxDeleteAge = 24 * 60 * 60 * 1000; // 24 hours
    return messageAge < maxDeleteAge;
  }

  /**
   * Delete a message
   */
  async deleteMessage(message: ChatMessage): Promise<void> {
    const messageId = message._id || message.id;
    if (!messageId) {
      return;
    }

    // Confirm deletion
    const confirmed = await Swal.fire({
      title: 'Delete message?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      target: 'body',
      customClass: {
        container: 'swal-top-container'
      }
    });

    if (!confirmed.isConfirmed) {
      return;
    }

    try {
      const result = await this.chatService.deleteChatMessage(messageId);
      if (result?.code === 200) {
        // Reload conversation to reflect deletion
        if (this.selectedContact) {
          await this.loadConversation(this.selectedContact);
        }
        this.showToast('Message deleted', 'success');
      } else {
        this.showToast('Failed to delete message', 'error');
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      this.showToast('Failed to delete message', 'error');
    }
  }

  /**
   * Handle key press in edit mode
   */
  onEditKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.saveEditedMessage();
    }
  }

  clearChat(): void {
    if (this.selectedContact) {
      // Clear current conversation
      this.currentConversationMessages = [];
      this.selectedContact = null;
    } else {
      this.chatService.clearMessages();
    }
    // Re-apply background after clearing
    setTimeout(() => {
      this.applyBackground();
    }, 100);
  }

  formatTime(date: Date): string {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        setTimeout(() => {
          const element = this.messagesContainer.nativeElement;
          element.scrollTop = element.scrollHeight;
        }, 100);
      }
    } catch (err) {
      // Ignore scroll errors
    }
  }

  // User search methods
  toggleUserSearch(): void {
    this.showUserSearch = !this.showUserSearch;
    if (!this.showUserSearch) {
      // Clear search when closing
      this.clearSearch();
    } else {
      // Reset header visibility when opening search panel
      this.showSearchPanelHeader = true;
      // Reload connection requests to ensure away indicators are up to date
      this.loadConnectionRequests();

      // Focus search input after panel opens
      setTimeout(() => {
        this.searchInput?.nativeElement?.focus();
      }, 100);
    }
  }

  /**
   * Clear search state
   */
  clearSearch(): void {
    // Cancel any pending search
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }

    // Increment request ID to invalidate any in-flight requests
    this.searchRequestId++;

    this.searchQuery = '';
    this.searchResults = [];
    this.isSearching = false;

    // Restore focus after clearing
    setTimeout(() => {
      const input = this.searchInput?.nativeElement;
      if (input) {
        input.focus();
      }
    }, 0);
  }

  toggleSearchPanelHeader(): void {
    this.showSearchPanelHeader = !this.showSearchPanelHeader;
  }

  /**
   * Handle search input blur
   */
  onSearchBlur(): void {
    // Don't lose focus if clicking on search results or clear button
    // This will be handled by the click events on those elements
  }

  /**
   * Handle search input focus
   */
  onSearchFocus(): void {
    // Ensure input stays focused
    const input = this.searchInput?.nativeElement;
    if (input) {
      // Small delay to ensure focus is maintained
      setTimeout(() => {
        if (document.activeElement !== input) {
          input.focus();
        }
      }, 0);
    }
  }

  onSearchInput(): void {
    // Save cursor position to restore later
    const input = this.searchInput?.nativeElement;
    const cursorPosition = input?.selectionStart || 0;

    // Clear previous debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }

    // Clear results if query is too short
    if (!this.searchQuery.trim() || this.searchQuery.trim().length < 2) {
      this.searchResults = [];
      this.isSearching = false;
      // Restore focus immediately
      setTimeout(() => {
        if (input) {
          input.focus();
          input.setSelectionRange(cursorPosition, cursorPosition);
        }
      }, 0);
      return;
    }

    // Show loading state immediately for better UX
    this.isSearching = true;

    // Debounce the search - wait 300ms after user stops typing
    this.searchDebounceTimer = setTimeout(() => {
      this.performSearch(cursorPosition);
      this.searchDebounceTimer = null;
    }, 300);
  }

  /**
   * Perform the actual search
   */
  private async performSearch(savedCursorPosition?: number): Promise<void> {
    const query = this.searchQuery.trim();
    const input = this.searchInput?.nativeElement;

    // Double-check query is still valid
    if (!query || query.length < 2) {
      this.searchResults = [];
      this.isSearching = false;
      // Restore focus
      setTimeout(() => {
        if (input && savedCursorPosition !== undefined) {
          input.focus();
          input.setSelectionRange(savedCursorPosition, savedCursorPosition);
        }
      }, 0);
      return;
    }

    // Increment request ID and store it for this request
    this.searchRequestId++;
    const currentRequestId = this.searchRequestId;

    try {
      const results = await this.chatService.searchUsers(query, this.currentUserId);

      // Check if this request is still valid (not superseded by a newer search)
      if (currentRequestId !== this.searchRequestId) {
        // This request is outdated, ignore results
        return;
      }

      // Additional client-side filter to ensure current user is not in results
      // and exclude users with pending connection requests (use userId / _id, not email)
      this.searchResults = results.filter(user => {
        const userId = user.userId || user._id || user.sub;

        // Exclude current user
        if (userId === this.currentUserId || user.email === this.currentUserId) {
          return false;
        }

        // Exclude users with pending connection requests sent by current user
        const hasPendingRequest = this.connectionRequests.some(
          req => req.fromUserId === this.currentUserId &&
            req.toUserId === userId &&
            req.status === 'pending'
        );

        return !hasPendingRequest;
      });

      // Restore focus after results update
      setTimeout(() => {
        if (input) {
          input.focus();
          // Restore cursor position if provided, otherwise place at end
          if (savedCursorPosition !== undefined) {
            const maxPosition = input.value.length;
            const position = Math.min(savedCursorPosition, maxPosition);
            input.setSelectionRange(position, position);
          } else {
            input.setSelectionRange(input.value.length, input.value.length);
          }
        }
      }, 0);
    } catch (error: any) {
      // Check if this request is still valid
      if (currentRequestId !== this.searchRequestId) {
        // This request is outdated, ignore error
        return;
      }

      console.error('Error searching users:', error);
      this.searchResults = [];
      this.showToast('Failed to search users. Please try again.');

      // Restore focus even on error
      setTimeout(() => {
        if (input) {
          input.focus();
          if (savedCursorPosition !== undefined) {
            input.setSelectionRange(savedCursorPosition, savedCursorPosition);
          }
        }
      }, 0);
    } finally {
      // Only update loading state if this request is still current
      if (currentRequestId === this.searchRequestId) {
        this.isSearching = false;
      }
    }
  }

  async sendConnectionRequest(user: any): Promise<void> {
    try {
      const toUserId = user.userId || user._id || user.sub;
      if (!toUserId) {
        this.showToast('Invalid user', 'error');
        return;
      }
      const result = await this.chatService.sendConnectionRequest(toUserId, "Hello, I would like to connect with you");
      if (result?.code === 201) {
        // Reload connection requests to update status
        await this.loadConnectionRequests();

        // Remove user from search results to prevent duplicate requests
        this.searchResults = this.searchResults.filter(
          u => (u.userId || u._id || u.sub) !== toUserId
        );

        // Show success toast notification
        this.showToast(`Connection request sent to ${user.name || user.email}!`, 'success');
      } else if (result?.code === 409) {
        this.showToast('Connection request already sent', 'error');
        await this.loadConnectionRequests();

        // Remove user from search results since request already exists
        this.searchResults = this.searchResults.filter(
          u => (u.userId || u._id || u.sub) !== toUserId
        );
      }
    } catch (error) {
      console.error('Error sending connection request:', error);
      this.showToast('Failed to send connection request', 'error');
    }
  }

  /**
   * Cancel a connection request from search results by userId
   */
  async cancelConnectionRequestByUserId(userId: string): Promise<void> {
    try {
      // Find the user in search results by userId (_id, sub, or email)
      const user = this.searchResults.find(
        u => (u.userId || u._id || u.sub) === userId || u.email === userId
      );

      if (!user) {
        this.showToast('User not found in search results', 'error');
        return;
      }

      const toUserId = user.userId || user._id || user.sub;
      const request = this.connectionRequests.find(
        req => req.fromUserId === this.currentUserId &&
          req.toUserId === toUserId &&
          req.status === 'pending'
      );

      if (!request) {
        this.showToast('Connection request not found', 'error');
        return;
      }

      const result = await this.chatService.cancelConnectionRequest(request._id);
      if (result?.code === 200) {
        await this.loadConnectionRequests();

        this.searchResults = this.searchResults.filter(
          u => (u.userId || u._id || u.sub) !== toUserId
        );

        this.showToast('Connection request cancelled', 'success');
      } else {
        this.showToast('Failed to cancel connection request', 'error');
      }
    } catch (error) {
      console.error('Error cancelling connection request:', error);
      this.showToast('Failed to cancel connection request', 'error');
    }
  }

  private showToast(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      }
    });
    const options: { icon: 'success' | 'error' | 'info'; title: string; background?: string; color?: string } = {
      icon: type,
      title: message
    };
    switch (type) {
      case 'success':
        options.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
        options.color = '#fff';
        break;
      case 'error':
      case 'info':
        break;
    }
    Toast.fire(options);
  }

  private showNotificationToast(title: string, message: string, icon: 'info' | 'success' | 'warning' = 'info'): void {
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      confirmButtonText: 'View',
      confirmButtonColor: icon === 'success' ? '#10b981' : '#667eea',
      timer: 5000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      }
    });
    Toast.fire({
      icon: icon,
      title: title,
      text: message,
      background: icon === 'success'
        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff',
      customClass: {
        popup: 'notification-toast'
      }
    });
  }

  async loadConnectionRequests(): Promise<void> {
    try {
      this.connectionRequests = await this.chatService.getConnectionRequests();
    } catch (error) {
      console.error('Error loading connection requests:', error);
    }
  }

  async respondToRequest(request: any, action: 'accepted' | 'rejected'): Promise<void> {
    try {
      await this.chatService.respondToConnectionRequest(request._id, action);
      await this.loadConnectionRequests();
      if (action === 'accepted') {
        this.showToast('Connection request accepted!', 'success');
        await this.loadContacts();
      }
    } catch (error) {
      console.error('Error responding to connection request:', error);
      this.showToast('Failed to respond to connection request', 'error');
    }
  }

  /**
   * Block/deny a connection request
   */
  async blockRequest(request: any): Promise<void> {
    try {
      const result = await this.chatService.blockConnectionRequest(request._id);
      if (result?.code === 200) {
        await this.loadConnectionRequests();
        this.showToast('Connection request blocked', 'success');
      } else {
        this.showToast('Failed to block connection request', 'error');
      }
    } catch (error) {
      console.error('Error blocking connection request:', error);
      this.showToast('Failed to block connection request', 'error');
    }
  }

  /**
   * Toggle between contacts and requests view
   */
  toggleView(): void {
    this.showRequests = !this.showRequests;
  }

  async deleteReceivedRequest(request: any, action: string): Promise<void> {
    try {
      const result = await this.chatService.deleteReceivedRequest(request._id, action);
      if (result?.code === 200) {
        await this.loadConnectionRequests();
        this.showToast('Connection request removed', 'success');
      } else {
        this.showToast('Failed to remove connection request', 'error');
      }
    } catch (error) {
      console.error('Error deleting received connection request:', error);
      this.showToast('Failed to remove connection request', 'error');
    }
  }

  async cancelConnectionRequest(request: any): Promise<void> {
    try {
      const result = await this.chatService.cancelConnectionRequest(request._id);
      if (result?.code === 200) {
        await this.loadConnectionRequests();
        this.showToast('Connection request cancelled', 'success');
      } else {
        this.showToast('Failed to cancel connection request', 'error');
      }
    } catch (error) {
      console.error('Error cancelling connection request:', error);
      this.showToast('Failed to cancel connection request', 'error');
    }
  }

  isRequestSent(userId: string): boolean {
    return this.connectionRequests.some(
      req => req.fromUserId === this.currentUserId && req.toUserId === userId && req.status === 'pending'
    );
  }

  isConnectionAccepted(userId: string): boolean {
    return this.connectionRequests.some(
      req =>
        ((req.fromUserId === this.currentUserId && req.toUserId === userId) ||
          (req.fromUserId === userId && req.toUserId === this.currentUserId)) &&
        req.status === 'accepted'
    );
  }

  getSentRequests(): any[] {
    return this.connectionRequests.filter(
      req => req.fromUserId === this.currentUserId && req.status === 'pending'
    );
  }

  getReceivedRequests(): any[] {
    return this.connectionRequests.filter(
      req => req.toUserId === this.currentUserId && req.status === 'pending'
    );
  }

  /**
   * Get all connection requests (sent and received) in a unified flow
   */
  getAllConnectionRequests(): any[] {
    const sent = this.getSentRequests();
    const received = this.getReceivedRequests();
    // Combine and sort by creation date (newest first)
    return [...received, ...sent].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });
  }

  /**
   * Check if a request is sent by current user
   */
  isSentRequest(request: any): boolean {
    return request.fromUserId === this.currentUserId;
  }

  /**
   * Group messages by date
   */
  getGroupedMessages(): Array<{ date: string; messages: ChatMessage[] }> {
    if (!this.currentConversationMessages || this.currentConversationMessages.length === 0) {
      return [];
    }

    const grouped: Map<string, ChatMessage[]> = new Map();

    this.currentConversationMessages.forEach(message => {
      const messageDate = new Date(message.timestamp);
      const dateKey = this.getDateKey(messageDate);

      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(message);
    });

    // Convert to array and sort by date (oldest first)
    return Array.from(grouped.entries())
      .map(([date, messages]) => ({
        date,
        messages: messages.sort((a, b) => {
          const dateA = new Date(a.timestamp).getTime();
          const dateB = new Date(b.timestamp).getTime();
          return dateA - dateB;
        })
      }))
      .sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return dateA - dateB;
      });
  }

  /**
   * Get date key for grouping (YYYY-MM-DD format)
   */
  private getDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Format date header for display
   */
  formatDateHeader(dateKey: string): string {
    const date = new Date(dateKey);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Reset time to compare dates only
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

    if (dateOnly.getTime() === todayOnly.getTime()) {
      return 'Today';
    } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
      return 'Yesterday';
    } else {
      // Format as "Month Day, Year" (e.g., "December 15, 2023")
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }

  /**
   * Check if we should show a date header before this message
   */
  shouldShowDateHeader(message: ChatMessage, previousMessage: ChatMessage | null): boolean {
    if (!previousMessage) {
      return true; // Always show header for first message
    }

    const messageDate = new Date(message.timestamp);
    const previousDate = new Date(previousMessage.timestamp);

    return this.getDateKey(messageDate) !== this.getDateKey(previousDate);
  }

  /**
   * Toggle contact menu
   */
  toggleContactMenu(event: Event, contact: Contact): void {
    event.stopPropagation(); // Prevent contact selection
    this.openContactMenu = this.openContactMenu === contact.userId ? null : contact.userId;
  }

  /**
   * Unfriend a contact (remove connection)
   */
  async unfriendContact(contact: Contact, action: 'pending' | 'accepted' | 'rejected' | 'blocked' | 'cancelled'): Promise<void> {
    this.openContactMenu = null;

    const confirmed = await Swal.fire({
      title: 'UnFriend Contact?',
      text: `Are you sure you want to remove ${contact.name} from your contacts? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Unfriend',
      cancelButtonText: 'Cancel',
      target: 'body',
      customClass: {
        container: 'swal-top-container'
      }
    });

    if (!confirmed.isConfirmed) {
      return;
    }

    try {
      // Reload connection requests to ensure we have the latest data
      await this.loadConnectionRequests();

      // console.log('Unfriending contact:', contact);
      // console.log('Current userId:', this.currentUserId);
      // console.log('All connection requests:', this.connectionRequests);

      // Find the connection request with accepted status
      // Check both directions since either user could have sent the request
      // Note: contact.email is actually the userId (as per getAcceptedConnections implementation)
      const contactUserId = contact.userId || contact.email;

      // Optimistically remove from UI immediately
      this.contacts = this.contacts.filter(c => {
        const cUserId = c.userId || c.email;
        return cUserId !== contactUserId;
      });
      this.cdr.detectChanges(); // Force change detection

      // Clear chat if this contact is selected
      if (this.selectedContact?.userId === contact.userId || this.selectedContact?.email === contact.email) {
        this.clearChat();
      }

      // Delete the connection
      const result = await this.chatService.deleteConnection(contactUserId, action);

      if (result?.code === 200) {
        // Reload contacts to ensure sync with server
        await this.loadContacts();
        await this.loadConnectionRequests();
        this.cdr.detectChanges(); // Force change detection after reload

        this.showToast(`${contact.name} has been removed from your contacts`, 'success');
      } else {
        // If deletion failed, reload to restore the contact
        await this.loadContacts();
        this.cdr.detectChanges();
        this.showToast(result?.message || 'Failed to remove contact', 'error');
      }
    } catch (error) {
      console.error('Error unfriending contact:', error);
      this.showToast('Failed to remove contact');
    }
  }

  /**
   * Clear messages for a specific contact
   */
  async clearContactMessages(contact: Contact): Promise<void> {
    this.openContactMenu = null;

    const confirmed = await Swal.fire({
      title: 'Clear Messages?',
      text: `Are you sure you want to delete all messages with ${contact.name}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Clear',
      cancelButtonText: 'Cancel',
      target: 'body',
      customClass: {
        container: 'swal-top-container'
      }
    });

    if (!confirmed.isConfirmed) {
      return;
    }

    try {
      const result = await this.chatService.clearConversationMessages(this.currentUserId, contact.userId);
      if (result?.code === 200) {
        // Reload conversation if this contact is selected
        if (this.selectedContact?.userId === contact.userId) {
          await this.loadConversation(contact);
        }
        this.showToast('Messages cleared successfully', 'success');
      } else {
        this.showToast('Failed to clear messages', 'error');
      }
    } catch (error) {
      console.error('Error clearing messages:', error);
      this.showToast('Failed to clear messages', 'error');
    }
  }

  /**
   * Export messages for a specific contact
   */
  async exportContactMessages(contact: Contact): Promise<void> {
    this.openContactMenu = null;

    try {
      // Get all messages for this conversation
      const messages = await this.chatService.getConversationMessages(contact.userId);

      if (!messages || messages.length === 0) {
        this.showToast('No messages to export', 'info');
        return;
      }

      // Format messages for export
      const exportData = {
        contact: {
          name: contact.name,
          email: contact.email,
          userId: contact.userId
        },
        exportedAt: new Date().toISOString(),
        messageCount: messages.length,
        messages: messages.map(msg => ({
          timestamp: new Date(msg.timestamp).toISOString(),
          sender: msg.sender === 'user' ? 'You' : contact.name,
          text: msg.text,
          edited: msg.edited || false,
          attachment: msg.attachment ? {
            fileName: msg.attachment.fileName,
            fileSize: msg.attachment.fileSize,
            mimeType: msg.attachment.mimeType
          } : null
        }))
      };

      // Create JSON file
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `messages_${contact.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.showToast('Messages exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting messages:', error);
      this.showToast('Failed to export messages');
    }
  }

  /**
   * Handle right-click on messages container to show background menu
   */
  onMessagesContainerContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    // Calculate menu position relative to the container
    const container = this.messagesContainer?.nativeElement;
    if (!container) return;

    const rect = container.getBoundingClientRect();

    // Calculate position relative to container
    this.backgroundMenuPosition = {
      top: event.clientY - rect.top + container.scrollTop,
      left: event.clientX - rect.left + container.scrollLeft
    };

    // Ensure menu stays within container bounds
    const menuWidth = 200;
    const menuHeight = 300;

    if (this.backgroundMenuPosition.left + menuWidth > rect.width) {
      this.backgroundMenuPosition.left = rect.width - menuWidth - 10;
    }
    if (this.backgroundMenuPosition.top + menuHeight > rect.height + container.scrollTop) {
      this.backgroundMenuPosition.top = rect.height + container.scrollTop - menuHeight - 10;
    }
    if (this.backgroundMenuPosition.left < 10) {
      this.backgroundMenuPosition.left = 10;
    }
    if (this.backgroundMenuPosition.top < container.scrollTop + 10) {
      this.backgroundMenuPosition.top = container.scrollTop + 10;
    }

    this.showBackgroundMenu = true;
  }

  /**
   * Select a background option
   */
  selectBackground(backgroundId: string): void {
    this.selectedBackground = backgroundId;
    // Save to localStorage immediately
    this.saveBackgroundPreference();
    // Apply background immediately
    this.applyBackground();
    // Also apply after a short delay to ensure it sticks
    setTimeout(() => {
      this.applyBackground();
    }, 50);
    this.showBackgroundMenu = false;
  }

  /**
   * Get the background value for the selected option
   */
  getSelectedBackgroundValue(): string {
    const option = this.backgroundOptions.find(opt => opt.id === this.selectedBackground);
    return option?.value || '#f0f2f5';
  }

  /**
   * Apply the selected background to the messages container
   */
  applyBackground(): void {
    const container = this.messagesContainer?.nativeElement;
    if (container) {
      const bgValue = this.getSelectedBackgroundValue();
      // Apply directly to style for immediate effect
      container.style.background = bgValue;
      // Also set as CSS custom property for better persistence and fallback
      container.style.setProperty('--chat-background', bgValue);
      // Set data attribute as backup
      container.setAttribute('data-background', bgValue);
    }
  }

  /**
   * Load background preference from settings service
   */
  loadBackgroundPreference(): void {
    try {
      const chatSettings = this.settingsService.chatSettings();
      if (chatSettings?.background && this.backgroundOptions.some(opt => opt.id === chatSettings.background)) {
        this.selectedBackground = chatSettings.background;
      } else {
        // If no saved preference, use default and save it
        this.selectedBackground = 'default';
        this.saveBackgroundPreference();
      }
      // Apply background after DOM is ready
      // Use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        this.applyBackground();
        // Also apply after a short delay to ensure container exists
        setTimeout(() => {
          this.applyBackground();
        }, 100);
      });
    } catch (error) {
      console.error('Error loading background preference:', error);
      // Fallback to localStorage if settings service fails
      try {
        const saved = localStorage.getItem('chatBackground');
        if (saved && this.backgroundOptions.some(opt => opt.id === saved)) {
          this.selectedBackground = saved;
          // Migrate from localStorage to settings
          this.saveBackgroundPreference();
        }
      } catch (e) {
        console.error('Error loading from localStorage fallback:', e);
      }
    }
  }

  /**
   * Save background preference to settings service (which saves to profile/database)
   */
  saveBackgroundPreference(): void {
    try {
      // Save to settings service (this will save to database/profile)
      this.settingsService.updateChatSettings({
        background: this.selectedBackground
      });
      // Also save to localStorage as a backup/fallback
      try {
        localStorage.setItem('chatBackground', this.selectedBackground);
      } catch (e) {
        // Ignore localStorage errors, settings service is primary
      }
    } catch (error) {
      console.error('Error saving background preference:', error);
      // Fallback to localStorage if settings service fails
      try {
        localStorage.setItem('chatBackground', this.selectedBackground);
      } catch (e) {
        console.error('Error saving to localStorage fallback:', e);
      }
    }
  }

  /**
   * Get contacts that are not group chats (for group creation)
   */
  get nonGroupContacts(): Contact[] {
    return this.contacts.filter(c => !c.isGroup);
  }

  /**
   * Toggle create group modal
   */
  toggleCreateGroupModal(): void {
    this.showCreateGroupModal = !this.showCreateGroupModal;
    if (!this.showCreateGroupModal) {
      // Reset form when closing
      this.newGroupName = '';
      this.selectedParticipantsForGroup = [];
    }
  }

  /**
   * Toggle participant selection for group creation
   */
  toggleParticipantSelection(userId: string): void {
    const index = this.selectedParticipantsForGroup.indexOf(userId);
    if (index > -1) {
      this.selectedParticipantsForGroup.splice(index, 1);
    } else {
      this.selectedParticipantsForGroup.push(userId);
    }
  }

  /**
   * Check if participant is selected for group creation
   */
  isParticipantSelected(userId: string): boolean {
    return this.selectedParticipantsForGroup.includes(userId);
  }

  /**
   * Create a new group chat
   */
  async createGroupChat(): Promise<void> {
    if (!this.newGroupName.trim()) {
      this.showToast('Please enter a group name', 'error');
      return;
    }

    if (this.selectedParticipantsForGroup.length < 1) {
      this.showToast('Please select at least one participant', 'error');
      return;
    }

    try {
      // Include current user in participants
      const allParticipants = [this.currentUserId, ...this.selectedParticipantsForGroup];

      const result = await this.chatService.createGroupChat(
        this.newGroupName.trim(),
        allParticipants
      );

      if (result?.code === 201) {
        this.showToast('Group chat created successfully!', 'success');
        this.toggleCreateGroupModal();
        // Reload contacts to show the new group
        await this.loadContacts();
        // Select the newly created group
        const newGroup = this.contacts.find(c => c?.isGroup && c?.groupId === result.data?._id);
        if (newGroup) {
          await this.selectContact(newGroup);
        }
      } else {
        this.showToast(result?.message || 'Failed to create group chat', 'error');
      }
    } catch (error) {
      console.error('Error creating group chat:', error);
      this.showToast('Failed to create group chat', 'error');
    }
  }

  /**
   * Open edit group modal
   */
  openEditGroupModal(contact: Contact): void {
    if (!contact || !contact.isGroup || !contact.groupId) {
      this.showToast('Invalid group');
      return;
    }

    // Ensure we have a valid groupId
    const groupId = contact.groupId;
    if (!groupId || typeof groupId !== 'string') {
      this.showToast('Group ID is missing', 'error');
      return;
    }

    this.editingGroup = contact;
    this.editGroupName = contact.name || '';
    this.selectedParticipantsForEdit = [];

    // Load current group participants (excluding current user)
    // We'll need to get the full group details to see all participants
    this.loadGroupDetailsForEdit(groupId);

    this.openContactMenu = null; // Close the menu
    this.showEditGroupModal = true;
  }

  /**
   * Load group details for editing
   */
  async loadGroupDetailsForEdit(groupId: string): Promise<void> {
    if (!groupId || typeof groupId !== 'string') {
      console.error('Invalid groupId provided to loadGroupDetailsForEdit');
      return;
    }

    try {
      const groupChats = await this.chatService.getGroupChats();
      const group = groupChats.find(g => g._id === groupId);

      if (group && group.participants) {
        // Pre-select current participants (excluding current user)
        this.selectedParticipantsForEdit = group.participants
          .filter(p => p.userId && p.userId !== this.currentUserId)
          .map(p => p.userId);
      }
    } catch (error) {
      console.error('Error loading group details:', error);
    }
  }

  /**
   * Toggle edit group modal
   */
  toggleEditGroupModal(): void {
    this.showEditGroupModal = !this.showEditGroupModal;
    if (!this.showEditGroupModal) {
      // Reset form when closing
      this.editingGroup = null;
      this.editGroupName = '';
      this.selectedParticipantsForEdit = [];
    }
  }

  /**
   * Toggle participant selection for group editing
   */
  toggleEditParticipantSelection(userId: string): void {
    const index = this.selectedParticipantsForEdit.indexOf(userId);
    if (index > -1) {
      this.selectedParticipantsForEdit.splice(index, 1);
    } else {
      this.selectedParticipantsForEdit.push(userId);
    }
  }

  /**
   * Check if participant is selected for group editing
   */
  isEditParticipantSelected(userId: string): boolean {
    return this.selectedParticipantsForEdit.includes(userId);
  }

  /**
   * Update group chat
   */
  async updateGroupChat(): Promise<void> {
    if (!this.editingGroup || !this.editingGroup.groupId) {
      this.showToast('Invalid group');
      return;
    }

    if (!this.editGroupName.trim()) {
      this.showToast('Please enter a group name', 'error');
      return;
    }

    if (this.selectedParticipantsForEdit.length < 1) {
      this.showToast('Please select at least one participant', 'error');
      return;
    }

    try {
      // Include current user in participants
      const allParticipants = [this.currentUserId, ...this.selectedParticipantsForEdit];

      const result = await this.chatService.updateGroupChat(
        this.editingGroup.groupId,
        this.editGroupName.trim(),
        allParticipants
      );

      if (result?.code === 200) {
        // Store groupId before closing modal (which sets editingGroup to null)
        const updatedGroupId = this.editingGroup.groupId;
        const wasSelected = this.selectedContact?.groupId === updatedGroupId;

        this.showToast('Group chat updated successfully!', 'success');
        this.toggleEditGroupModal();

        // Reload contacts to show the updated group
        await this.loadContacts();

        // Select the updated group if it was selected before
        if (wasSelected) {
          const updatedGroup = this.contacts.find(c => c?.isGroup && c?.groupId === updatedGroupId);
          if (updatedGroup) {
            await this.selectContact(updatedGroup);
          }
        }
      } else {
        this.showToast(result?.message || 'Failed to update group chat', 'error');
      }
    } catch (error) {
      console.error('Error updating group chat:', error);
      this.showToast('Failed to update group chat', 'error');
    }
  }
}
